import { Link } from "react-router-dom";
import Breadcrumb, { Footer } from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import JsonLd from "@/components/base/JsonLd";
import useSeo from "@/hooks/useSeo";

const VALUES = [
  { icon: "ri-focus-3-line", t: "只对证据负责", d: "不讨好流量、不站任何一方，结论只建立在可追溯、可复现的公开证据之上。" },
  { icon: "ri-scales-3-line", t: "兼听并包", d: "主动寻找反方证据，不靠“反驳缺失”下结论，宁可标存疑也不抢着定案。" },
  { icon: "ri-eye-off-line", t: "隐私先行", d: "梳理事件不扩散无关个人隐私，给当事人保留澄清与回应的空间。" },
  { icon: "ri-community-line", t: "人人可学", d: "把判断方法做成大众能用的清单与指南，让理性求证不只属于专业人员。" },
];

const PRINCIPLES = [
  "先梳理，再定性：先铺开证据链，才谈得上“实锤还是辟谣”。",
  "来源必须可溯：无法溯源的信息降级处理，不直接采信。",
  "区分事实与观点：情绪表达与客观陈述分开，避免被叙事带偏。",
  "持续修正：新证据出现时，及时更新结论并标注变化。",
];

const NOT_DO = [
  "不为流量编造或夸大事件",
  "不传播未经脱敏的个人隐私与健康信息",
  "不接受付费“删帖”“定向引流”等交易",
  "不做任何个股、投资或消费的投喂式建议",
];

export default function About() {
  useSeo(
    "关于我们 · 辨真社 品牌定位与核验理念",
    "了解辨真社：专注免费爆料真实性核查与辟谣可信度分析的资讯门户。认识我们的定位、核验理念、内容原则与联系方式。"
  );

  return (
    <>
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "关于辨真社",
          url: window.location.origin + "/about",
          inLanguage: "zh-CN",
          about: { "@type": "Organization", name: "辨真社" },
        }}
      />
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-foreground-950">
          <div className="absolute inset-0 bg-gradient-to-b from-primary-900/40 to-foreground-950"></div>
          <div className="relative mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <div className="mt-8 max-w-3xl">
              <h1 className="font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
                让每一条爆料，<br />都经得起“求真”的检验
              </h1>
              <p className="mt-6 text-base leading-relaxed text-background-100/90 md:text-lg">
                辨真社是一群相信“真相经得起追问”的内容创作者搭建的资讯站点。当网络上的爆料、
                实锤与辟谣越来越难分辨，我们希望提供一个以证据链为核心的参照坐标——
                帮你先辨真伪，再决定要不要吃瓜。
              </p>
            </div>
          </div>
        </section>

        {/* 定位与故事 */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <span className="inline-flex items-center gap-2 text-sm font-semibold tracking-widest text-primary-700 uppercase">
                <i className="ri-compass-3-line" /> 我们的定位
              </span>
              <h2 className="mt-2 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                网络信息时代的“求证坐标”
              </h2>
            </div>
            <div className="space-y-5 text-base leading-loose text-foreground-800 lg:col-span-2">
              <p>
                每天有海量“免费爆料”在群聊、社区与短视频间流转。它们有些是真实线索，有些是营销脚本，
                有些干脆是 AI 合成的产物。区别它们，靠的既不是转发量，也不是情绪浓度，而是一套朴素的
                求证动作：溯源、对撞、复现、留证。
              </p>
              <p>
                辨真社的核心价值，就是把这套动作拆解成普通读者也能看懂的内容。我们不止告诉你“真假”，
                更尽量呈现“为什么这样判断”，让每一次求证都能沉淀成可复用的判断力，真正让你在吃瓜时
                不被带节奏，看清事件本质。
              </p>
            </div>
          </div>
        </section>

        {/* 理念 / 价值观 */}
        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">我们坚持的四条价值观</h2>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {VALUES.map((v) => (
                <div key={v.t} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className={`${v.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">{v.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{v.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 内容原则 + 红线 */}
        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700"><i className="ri-file-list-3-line" /></span>
                内容制作原则
              </h2>
              <ul className="mt-5 space-y-3">
                {PRINCIPLES.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-800 md:text-base">
                    <i className="ri-checkbox-circle-line mt-0.5 text-accent-600" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
              <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-100 text-primary-700"><i className="ri-error-warning-line" /></span>
                我们绝不做的事
              </h2>
              <ul className="mt-5 space-y-3">
                {NOT_DO.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-800 md:text-base">
                    <i className="ri-close-circle-line mt-0.5 text-primary-600" />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* 联系 */}
        <section className="border-t border-background-200 bg-secondary-100/60">
          <div className="mx-auto max-w-7xl px-4 py-12 text-center md:px-6">
            <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">一起让网络更清醒</h2>
            <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-foreground-700">
              遇到难辨的爆料想求证？对某个事件有补充证据？或想聊聊信息核查的方法——
              欢迎通过邮箱与我们联系。
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <span className="inline-flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-5 py-3 text-sm font-medium text-foreground-900">
                <i className="ri-mail-line text-primary-600" /> feedback@bianzhenshe.example
              </span>
              <Link to="/topics" className="inline-flex h-11 items-center justify-center whitespace-nowrap rounded-md bg-primary-600 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-500">
                看看最新核验 <i className="ri-arrow-right-line ml-2" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}